import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import loadable from '@loadable/component';

import { gettext } from '@eb/i18n';
import { Calendar as CalendarSVG } from '@eb/eds-iconography';

import { ORGANIZER_ARTICLE_TYPE } from '../../../../constants/constants';
import {
    BUCKETS_PROPTYPES,
    FEED_CONFIG_PROPTYPES,
    FEATURE_FLAGS_PROPTYPES,
    EVENT_SEARCH_PROPTYPES,
} from '../../../../constants/propTypes';
import { ALL_TAB } from '../../../../constants/tabConfig';

import {
    getFlatBucketContent,
    getBucketsContent,
    verifyPoorContent,
} from '../../redux/content';
import { getOrganizersUserFollow } from '../../redux/organizers';

import {
    retrieveOrgToFollow,
    removeOrgToFollow,
    getBuckets,
    buildBuckets,
} from '../../utils';

import { LoadingBucket } from '../../components/LoadingBucket';
import { EventsBucketContainer } from '../EventsBucketContainer';
import { LazyLoadedCollectionsBucket } from '../CollectionsBucketContainer';

const StateMessage = loadable(() => import('../../components/StateMessage'), {
    resolveComponent: (components) => components.StateMessage,
});

import './FeedBuilderContainer.scss';

const useIsInBrowser = () => {
    const [isInBrowser, setIsInBrowser] = React.useState(false);

    React.useEffect(() => {
        setIsInBrowser(true);
    }, []);

    return isInBrowser;
};

const useOrganizerFollowOnMount = () => {
    useEffect(() => {
        const organizerToFollow = retrieveOrgToFollow();

        if (organizerToFollow) {
            removeOrgToFollow();
        }
    }, []);
};

const useVerifyPoorContent = ({ verifyPoorContent, buckets, events }) => {
    useEffect(() => {
        if (verifyPoorContent) {
            verifyPoorContent({
                buckets,
                events,
                actionName: 'on_tab_loading',
            });
        }
    }, [verifyPoorContent, buckets, events]);
};

const FeedBuilderTabs = (props) => {
    const {
        tabKey,
        buckets,
        events,
        featureFlags,
        feedConfig,
        verifyPoorContent,
        loadingState: {
            isFeLoading,
            isSearchLoading,
            isCityBrowseLoading,
        } = {},
        shouldSuggestReloadingPage,
    } = props;

    const isInBrowser = useIsInBrowser();
    useOrganizerFollowOnMount();
    useVerifyPoorContent({ verifyPoorContent, buckets, events });

    const allFlatBuckets = buildBuckets({
        events,
        buckets,
        feedConfig,
        isCityBrowseLoading,
    });

    const allBuckets = getBuckets({
        buckets: allFlatBuckets,
        featureFlags,
        feedConfig,
    });

    let renderedBuckets = [
        <div className="feed-events-bucket" key="loading-component">
            <div
                data-testid="feed-events-bucket-content"
                className="feed-events-bucket__content feed-events-bucket__content--loading"
            >
                <LoadingBucket />
            </div>
        </div>,
    ];

    // Only show this when in browser, not server, to avoid hydration mismatch
    if (!isFeLoading && isInBrowser) {
        if (allBuckets.length === 0 && shouldSuggestReloadingPage) {
            // The server is temporarily unable to return events
            return (
                <StateMessage
                    title={gettext('Unable to load events')}
                    description={gettext('Try again or check back later')}
                    iconType={<CalendarSVG />}
                    iconSize="medium"
                    primaryLink="/"
                    primaryText={gettext('Try again')}
                />
            );
        }

        if (!isSearchLoading) {
            renderedBuckets = allBuckets.reduce(
                (memo, { key, type, bucketConfig, ...bucketProps }, index) => {
                    const lastIndex = allBuckets.length - 1;
                    const separator =
                        index < lastIndex ? (
                            <hr
                                key={`separator-${key}`}
                                className="feed-events-bucket__separator"
                            />
                        ) : null;

                    if (type === 'event') {
                        return [
                            ...memo,
                            <EventsBucketContainer
                                key={`events-${key}`}
                                bucketConfig={bucketConfig}
                                bucketKey={key}
                                shouldUseWaypoints={index > 0}
                                // Always show see more for "Organizers you follow"
                                shouldShowSeeMore={
                                    index === lastIndex ||
                                    key === 'followed_profiles'
                                }
                                featureFlags={featureFlags}
                                {...bucketProps}
                            />,
                            tabKey === ALL_TAB && index === 0 ? (
                                <LazyLoadedCollectionsBucket
                                    key={`collection-${key}`}
                                    tabKey={tabKey}
                                />
                            ) : null,
                            separator,
                        ];
                    }

                    return memo;
                },
                [],
            );
        }
    }

    return <div className="feed-builder">{renderedBuckets}</div>;
};

FeedBuilderTabs.propTypes = {
    getOrganizersUserFollow: PropTypes.func,
    buckets: BUCKETS_PROPTYPES.isRequired,
    events: BUCKETS_PROPTYPES.isRequired,
    featureFlags: FEATURE_FLAGS_PROPTYPES.isRequired,
    feedConfig: FEED_CONFIG_PROPTYPES.isRequired,
    eventSearch: EVENT_SEARCH_PROPTYPES,
    verifyPoorContent: PropTypes.func,
    shouldSuggestReloadingPage: PropTypes.bool,
    tabKey: PropTypes.string,
    isAuthenticated: PropTypes.bool,
    loadingState: PropTypes.shape({
        isFeLoading: PropTypes.bool,
        isSearchLoading: PropTypes.bool,
        isCityBrowseLoading: PropTypes.bool,
        isProfilesLoading: PropTypes.bool,
        isOrganizerArticlesLoading: PropTypes.bool,
    }),
};

const _mapStateToProps = ({ app, content, currencyFormat, user }) => ({
    buckets: getBucketsContent(content, currencyFormat),
    events: getFlatBucketContent(
        content.flatBucket,
        currencyFormat,
        {},
        content.browseState.tabKey,
    ),
    featureFlags: app.featureFlags,
    loadingState: app.loadingState,
    shouldSuggestReloadingPage: !!content.flatBucket
        .__should_suggest_reloading_page,
    tabKey: content.browseState.tabKey,
    isAuthenticated: user.isAuthenticated,
});

const _mapDispatchToProps = {
    verifyPoorContent,
    getOrganizersUserFollow,
};

export default connect(_mapStateToProps, _mapDispatchToProps)(FeedBuilderTabs);
